import { knownText } from "@nexxt/app-kit";
import { StatusBadge } from "@nexxt/ui";
import { m } from "../../../paraglide/messages.js";

// What tells two accounts with the same name apart (ADR-299 §6): the code of the customer on BC and the country.

interface Coded {
  customerCode?: string | null | undefined;
  countryCode?: string | null | undefined;
}

/** The code and the country of an account, as one line; empty when it has neither. */
export const accountCode = ({ customerCode, countryCode }: Coded): string =>
  [customerCode, countryCode].filter(Boolean).join(" · ");

const roleLabel: Record<string, () => string> = {
  main: m.contact_role_main,
  accounts_payable: m.contact_role_accounts_payable,
  buyer: m.contact_role_buyer,
  store_manager: m.contact_role_store_manager,
  brand_contact: m.contact_role_brand_contact,
  other: m.contact_role_other,
};

/** The role of a person of an account in words; the code of the directory for one this app has no words for. */
export const contactRoleText = (role: string): string => knownText(roleLabel, role);

/** The name of an account with its code and country after it, as a picker keeps what was chosen. */
export const accountLabel = (account: Coded & { name: string }): string => {
  const code = accountCode(account);
  return code ? `${account.name} (${code})` : account.name;
};

/** The initials of a name: the first letters of its first two words. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return (
    words
      .slice(0, 2)
      .map((w) => [...w][0] ?? "")
      .join("")
      .toLocaleUpperCase() || "?"
  );
}

/** The badge of an account in a list: its initials, the name beside it says who (decoration for a screen reader). */
export function AccountInitials({ name, brand = false }: { name: string; brand?: boolean }) {
  return (
    <span
      aria-hidden
      className={
        brand
          ? "grid size-9 shrink-0 place-items-center rounded-md border border-border bg-background text-xs font-semibold text-foreground"
          : "grid size-9 shrink-0 place-items-center rounded-full bg-muted text-xs font-semibold text-foreground"
      }
    >
      {initials(name)}
    </span>
  );
}

/** What an account is to Tomorrow: a customer, a brand, or both. */
export function AccountKinds({ account }: { account: { isCustomer: boolean; isBrand: boolean } }) {
  return (
    <>
      {account.isCustomer && <StatusBadge tone="info" label={m.account_customer()} />}
      {account.isBrand && <StatusBadge tone="neutral" label={m.account_brand()} />}
    </>
  );
}
